import Paper from "@mui/material/Paper";
import classes from "../../styles/DashboardView.module.css";
import { numberWithCommas } from "../Oversigt";
import { KpiComparison, formatIndexRatio } from "../../utilities/dashboardAggregations";

interface KpiCardsProps {
    totalRevenue: number;
    totalRegistrations: number;
    avgRevenue: number;
    avgParticipants: number;
    topCourse: string;
    topCourseCount: number;
    repeatRate: number;
    prevTopCourse: string;
    prevTopCourseCount: number;
    prevYearLabel: string;
    comparison: {
        totalRevenue: KpiComparison;
        totalRegistrations: KpiComparison;
        avgRevenue: KpiComparison;
        avgParticipants: KpiComparison;
        repeatRate: KpiComparison;
    };
}

type Trend = "up" | "down" | "flat" | "none";

function trendOf(cmp: KpiComparison): Trend {
    if (cmp.index === null) return "none";
    if (cmp.index > 102) return "up";
    if (cmp.index < 98) return "down";
    return "flat";
}

function indexDisplay(cmp: KpiComparison): string | null {
    return formatIndexRatio(cmp.current, cmp.previous);
}

function formatPct(value: number | null): string {
    if (value === null) return "—";
    const sign = value > 0 ? "+" : "";
    return `${sign}${value.toString().replace(".", ",")}%`;
}

function formatPp(current: number, previous: number, hasPrev: boolean): string {
    if (!hasPrev) return "—";
    const diff = Math.round((current - previous) * 10) / 10;
    const sign = diff > 0 ? "+" : "";
    return `${sign}${diff.toString().replace(".", ",")} pp`;
}

function arrow(t: Trend): string {
    if (t === "up") return "▲";
    if (t === "down") return "▼";
    return "–";
}

const fmt = (n: number): string => String(numberWithCommas(n) ?? 0);

interface KpiCardProps {
    label: string;
    value: string;
    unit?: string;
    cmp: KpiComparison;
    prevFormatted: string;
    prevYearLabel: string;
    deltaText?: string;
}

function KpiCard({ label, value, unit, cmp, prevFormatted, prevYearLabel, deltaText }: KpiCardProps) {
    const t = trendOf(cmp);
    const deltaDisplay = deltaText ?? formatPct(cmp.deltaPct);

    return (
        <Paper className={`${classes.kpiCard} ${classes[`trend_${t}`]}`} elevation={2}>
            <div className={classes.kpiValueRow}>
                <span className={classes.kpiValue}>{value}</span>
                {unit && <span className={classes.kpiUnit}>{unit}</span>}
            </div>
            <div className={classes.kpiLabel}>{label}</div>
            <div className={classes.kpiCompare}>
                <span className={classes.kpiDelta}>
                    <span className={classes.kpiArrow}>{arrow(t)}</span>
                    {deltaDisplay}
                </span>
                {indexDisplay(cmp) !== null && (
                    <span className={classes.kpiIndex}>· Indeks {indexDisplay(cmp)}</span>
                )}
            </div>
            <div className={classes.kpiPrev}>{prevYearLabel}: {prevFormatted}</div>
        </Paper>
    );
}

export function KpiCards({
    totalRevenue,
    totalRegistrations,
    avgRevenue,
    avgParticipants,
    topCourse,
    topCourseCount,
    repeatRate,
    prevTopCourse,
    prevTopCourseCount,
    prevYearLabel,
    comparison,
}: KpiCardsProps) {
    const repeatCmp = comparison.repeatRate;
    const repeatDeltaText = formatPp(repeatRate, repeatCmp.previous, repeatCmp.index !== null);

    return (
        <div className={classes.kpiGrid}>
            <KpiCard
                label="Samlet omsætning"
                value={fmt(totalRevenue)}
                unit="kr."
                cmp={comparison.totalRevenue}
                prevFormatted={`${fmt(comparison.totalRevenue.previous)} kr.`}
                prevYearLabel={prevYearLabel}
            />
            <KpiCard
                label="Tilmeldinger"
                value={fmt(totalRegistrations)}
                cmp={comparison.totalRegistrations}
                prevFormatted={fmt(comparison.totalRegistrations.previous)}
                prevYearLabel={prevYearLabel}
            />
            <KpiCard
                label="Gns. pr. tilmelding"
                value={fmt(avgRevenue)}
                unit="kr."
                cmp={comparison.avgRevenue}
                prevFormatted={`${fmt(comparison.avgRevenue.previous)} kr.`}
                prevYearLabel={prevYearLabel}
            />
            <KpiCard
                label="Gns. deltagere"
                value={avgParticipants.toFixed(1).replace(".", ",")}
                cmp={comparison.avgParticipants}
                prevFormatted={comparison.avgParticipants.previous.toFixed(1).replace(".", ",")}
                prevYearLabel={prevYearLabel}
            />
            <KpiCard
                label="Gentagne kunder"
                value={repeatRate.toString()}
                unit="%"
                cmp={comparison.repeatRate}
                prevFormatted={`${repeatCmp.previous}%`}
                prevYearLabel={prevYearLabel}
                deltaText={repeatDeltaText}
            />
            <Paper className={`${classes.kpiCard} ${classes.kpiCardTop}`} elevation={2}>
                <div className={classes.kpiTopCourse}>{topCourse}</div>
                <div className={classes.kpiLabel}>Mest populære kursus</div>
                {topCourseCount > 0 && (
                    <div className={classes.kpiCompare}>
                        <span className={classes.kpiTopCount}>{topCourseCount} tilmelding{topCourseCount === 1 ? "" : "er"}</span>
                    </div>
                )}
                <div className={classes.kpiPrev}>
                    {prevYearLabel}: {prevTopCourse || "—"}
                    {prevTopCourseCount > 0 ? ` (${prevTopCourseCount})` : ""}
                </div>
            </Paper>
        </div>
    );
}
